import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Send, Sparkles, PowerOff, Loader2 } from "lucide-react";
import { PageShell, Eyebrow } from "../components/Layout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERSONAS = [
  { key: "matilda", initial: "M", name: "Matilda", role: "General GRC", hint: "Frameworks, ERM, board reporting" },
  { key: "arjun", initial: "A", name: "Arjun", role: "Privacy / OAIC", hint: "Privacy Act, APPs, NDB scheme" },
  { key: "sophie", initial: "S", name: "Sophie", role: "Money Flow", hint: "RBA, NPP, AML/CTF, corridors" },
  { key: "jack", initial: "J", name: "Jack", role: "APRA Regs", hint: "CPS 234, CPS 230, FAR" },
];

const SUGGESTIONS = {
  matilda: ["Compare ISO 31000 and COSO ERM for an ASX-listed insurer", "How do I build a board risk appetite statement?"],
  arjun: ["Walk me through the NDB 30-day assessment", "What changes with the statutory privacy tort?"],
  sophie: ["What does Tranche 2 mean for a remitter?", "Explain the RBA's current policy stance"],
  jack: ["What will APRA probe first under CPS 230?", "CPS 234 incident notification timelines?"],
};

const getSessionId = () => {
  let sid = localStorage.getItem("rp_chat_session");
  if (!sid) {
    sid = `sess-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem("rp_chat_session", sid);
  }
  return sid;
};

const Advisor = () => {
  const [aiStatus, setAiStatus] = useState(null); // {enabled, model_label}
  const [persona, setPersona] = useState("matilda");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const bottomRef = useRef(null);
  const sessionId = useRef(getSessionId());

  useEffect(() => {
    axios.get(`${API}/settings/ai`).then((r) => setAiStatus(r.data)).catch(() => setAiStatus({ enabled: false }));
  }, []);

  useEffect(() => {
    setHistoryLoading(true);
    axios.get(`${API}/chat/${sessionId.current}`)
      .then((r) => setMessages(r.data.filter((m) => m.persona === persona).map((m) => ({ role: m.role, content: m.content }))))
      .catch(() => setMessages([]))
      .finally(() => setHistoryLoading(false));
  }, [persona]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streaming]);

  const send = useCallback(async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, persona, message: msg }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const detail = err?.detail || "The advisor is unavailable right now.";
        setMessages((prev) => {
          const next = [...prev];
          next[next.length - 1] = { role: "assistant", content: detail, error: true };
          return next;
        });
        if (res.status === 503) setAiStatus((s) => ({ ...(s || {}), enabled: false }));
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          try {
            const evt = JSON.parse(line.slice(5).trim());
            if (evt.delta) {
              setMessages((prev) => {
                const next = [...prev];
                next[next.length - 1] = { role: "assistant", content: next[next.length - 1].content + evt.delta };
                return next;
              });
            } else if (evt.error) {
              setMessages((prev) => {
                const next = [...prev];
                next[next.length - 1] = { role: "assistant", content: evt.error, error: true };
                return next;
              });
            }
          } catch (_) { /* partial frame */ }
        }
      }
    } catch (e) {
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = { role: "assistant", content: "Connection lost — please try again.", error: true };
        return next;
      });
    } finally {
      setStreaming(false);
    }
  }, [input, persona, streaming]);

  const active = PERSONAS.find((p) => p.key === persona);

  return (
    <PageShell>
      <section className="hero-grid border-b border-neutral-200">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 pt-12 pb-8">
          <Eyebrow>AI Advisor {aiStatus?.model_label ? `· Powered by ${aiStatus.model_label}` : ""}</Eyebrow>
          <h1 className="font-heading text-4xl lg:text-[52px] font-bold tracking-tight text-ink leading-[1.05]">Four advisors. One panel.<br />Australia-aware answers.</h1>
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
          {aiStatus && !aiStatus.enabled ? (
            <div className="border border-neutral-200 rounded-lg p-12 text-center max-w-2xl mx-auto" data-testid="advisor-disabled">
              <PowerOff size={40} className="text-neutral-300 mx-auto" />
              <h2 className="font-heading text-2xl font-bold text-ink mt-5">The AI Advisor is currently offline.</h2>
              <p className="text-[14.5px] text-ink/55 mt-3 leading-relaxed">The administrator has temporarily disabled the advisor panel. The encyclopedia, hubs and tools remain fully available — please check back soon.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Persona rail */}
              <div className="lg:col-span-3 flex lg:flex-col gap-3 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
                {PERSONAS.map((p) => (
                  <button key={p.key} onClick={() => setPersona(p.key)} data-testid={`persona-${p.key}`}
                    className={`shrink-0 lg:shrink text-left border rounded-lg p-4 flex items-center gap-3 transition-colors duration-200 min-w-[200px] lg:min-w-0 ${persona === p.key ? "border-accent bg-accent/[0.04]" : "border-neutral-200 hover:border-neutral-400 bg-white"}`}>
                    <span className={`w-10 h-10 rounded-full font-heading font-bold flex items-center justify-center shrink-0 ${persona === p.key ? "bg-accent text-white" : "bg-ink text-white"}`}>{p.initial}</span>
                    <span>
                      <span className="block font-heading font-bold text-ink text-[15px]">{p.name}</span>
                      <span className="block font-mono text-[10px] tracking-wide text-neutral-400 uppercase mt-0.5">{p.role}</span>
                    </span>
                  </button>
                ))}
                <div className="hidden lg:block border-l-[3px] border-accent bg-paper rounded-r-lg p-4 mt-2">
                  <p className="text-[12px] text-ink/55 leading-relaxed">General information only — not legal or financial advice. Answers may be imperfect; verify against the current instruments.</p>
                </div>
              </div>

              {/* Chat panel */}
              <div className="lg:col-span-9 border border-neutral-200 rounded-lg flex flex-col bg-white overflow-hidden" style={{ height: "640px" }}>
                <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between bg-paper">
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-full bg-accent text-white font-heading font-bold text-sm flex items-center justify-center">{active.initial}</span>
                    <div>
                      <p className="font-heading font-bold text-ink text-[15px] leading-none">{active.name}</p>
                      <p className="font-mono text-[10px] text-neutral-400 uppercase tracking-wide mt-1">{active.hint}</p>
                    </div>
                  </div>
                  <span className="hidden sm:inline-flex items-center gap-1.5 font-mono text-[10px] text-emerald-600 uppercase tracking-widest"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Online</span>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5" data-testid="chat-messages">
                  {historyLoading ? (
                    <div className="flex items-center gap-2 text-neutral-400 text-sm"><Loader2 size={15} className="animate-spin" /> Loading conversation…</div>
                  ) : messages.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-center px-6">
                      <Sparkles size={28} className="text-accent mb-4" />
                      <p className="font-heading text-lg font-bold text-ink">Ask {active.name} anything.</p>
                      <p className="text-[13px] text-ink/50 mt-1.5 max-w-sm">Multi-turn, persona-aware, Australia-first. Try one of these:</p>
                      <div className="flex flex-col gap-2 mt-5 w-full max-w-md">
                        {SUGGESTIONS[persona].map((s) => (
                          <button key={s} onClick={() => send(s)} data-testid="chat-suggestion"
                            className="border border-neutral-200 rounded-md px-4 py-2.5 text-[13px] text-ink/70 hover:border-accent hover:text-accent transition-colors duration-150 text-left">{s}</button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    messages.map((m, i) => (
                      <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[85%] rounded-lg px-4 py-3 text-[14px] leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-ink text-white" : m.error ? "bg-red-50 text-red-700 border border-red-200" : "bg-paper text-ink border border-neutral-200"}`}>
                          {m.content || (streaming && i === messages.length - 1 ? <span className="inline-flex gap-1 items-center text-neutral-400"><Loader2 size={13} className="animate-spin" /> thinking…</span> : "")}
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={bottomRef} />
                </div>

                <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-neutral-200 p-4 flex gap-3">
                  <input value={input} onChange={(e) => setInput(e.target.value)} data-testid="chat-input"
                    placeholder={`Message ${active.name}…`} disabled={streaming}
                    className="flex-1 border border-neutral-300 rounded-md px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-accent disabled:bg-neutral-50" />
                  <button type="submit" disabled={streaming || !input.trim()} data-testid="chat-send-btn"
                    className="bg-accent text-white rounded-md px-5 flex items-center gap-2 text-sm font-semibold hover:bg-accent-dark transition-colors duration-200 disabled:opacity-40">
                    {streaming ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                    <span className="hidden sm:inline">Send</span>
                  </button>
                </form>
              </div>
            </div>
          )}
        </div>
      </section>
    </PageShell>
  );
};

export default Advisor;
